/**
 * Le règlement d'une inscription, tel que les listes de présences l'affichent.
 *
 * ## Il vient de la facture, pas de `payment_status`
 *
 * La colonne « Statut » des listes de présences lisait
 * `registrations.payment_status`, que le circuit des paiements ne met plus à
 * jour : une facture est soldée par ses affectations, pas par ce champ.
 * Mesuré le 17 octobre 2026, aucune des 4 243 inscriptions 2026 n'y était
 * « paid », alors que 3 067 ont une facture soldée — d'où « En attente » sur
 * toutes les lignes.
 *
 * Le statut vient donc de `reglement_des_inscriptions`, qui rend pour chaque
 * inscription sa facture et le `settlement` calculé par `v_invoice_balances` :
 * le même que l'écran des factures, dans les mêmes mots.
 *
 * ## Trois cas que l'écran ne doit pas confondre
 *
 * - **inscription annulée** : elle l'emporte, comme avant. L'enfant ne vient
 *   pas ; ce que devient l'argent se règle sur la facture. Elle se lit sur
 *   `cancellation_status`, et sur `payment_status = 'cancelled'` — une
 *   commande abandonnée : c'est le seul sens que la base garde à ce champ
 *   (`registrations_une_inscription_active`, `create_order`).
 * - **sans facture** : la base ne relie l'inscription à aucune facture. C'est
 *   un fait à montrer, pas un « impayé ».
 * - **indisponible** : la lecture a échoué. On le dit, plutôt que de retomber
 *   sur `payment_status`, qui afficherait de nouveau « En attente » partout.
 *
 * Les écrans qui montrent déjà l'annulation dans une colonne à part
 * (modification d'une inscription, demandes d'annulation) affichent la
 * facture : `reglementACoteDeLAnnulation`. Les autres,
 * `reglementDeLInscription`.
 *
 * Les lignes d'une inscription valent `undefined` pendant la lecture, `null`
 * si elle a échoué, un tableau (vide sans facture) une fois lues.
 */
import { ETATS, type Settlement } from '../factures/types';

/** Une ligne de `reglement_des_inscriptions`. */
export interface LigneDeReglement {
  registrationId: string;
  invoiceNumber: string;
  settlement: Settlement;
}

export type CleDeReglement =
  | Settlement
  | 'inscription_annulee'
  | 'sans_facture'
  | 'indisponible'
  | 'lecture';

/** Les lignes d'une inscription : en lecture, illisibles, ou lues. */
export type LignesLues = readonly LigneDeReglement[] | null | undefined;

/** Les factures des inscriptions lues, par identifiant d'inscription. */
export type CarteDesReglements = ReadonlyMap<string, readonly LigneDeReglement[]>;

/**
 * Les lignes d'une inscription dans une carte : `undefined` tant que la carte
 * se lit, `null` si elle n'a pas pu l'être, `[]` pour une inscription sans
 * facture.
 */
export function lignesDe(carte: CarteDesReglements | null | undefined, registrationId: string): LignesLues {
  if (carte === undefined) return undefined;
  if (carte === null) return null;
  return carte.get(registrationId) ?? [];
}

export interface ReglementAffiche {
  cle: CleDeReglement;
  libelle: string;
  classe: string;
  /** La phrase de l'infobulle : la facture lue, et ce que son état veut dire. */
  titre: string;
}

const LIBELLES: Record<Settlement, { libelle: string; classe: string }> = {
  paid: { libelle: 'Payé', classe: 'bg-green-100 text-green-800' },
  partial: { libelle: 'Partiel', classe: 'bg-orange-100 text-orange-800' },
  unpaid: { libelle: 'Impayé', classe: 'bg-yellow-100 text-yellow-800' },
  credited: { libelle: 'Crédité', classe: 'bg-indigo-100 text-indigo-800' },
  cancelled: { libelle: 'Facture annulée', classe: 'bg-gray-200 text-gray-700' },
};

/**
 * L'ordre de lecture quand une inscription porte plusieurs factures.
 *
 * Aucune ne le fait en production (17/10/2026), mais les deux liens
 * inscription → facture ont déjà divergé. Une facture vivante l'emporte sur
 * une annulée, et la moins réglée l'emporte : dire « Payé » sur la foi d'une
 * facture quand une autre attend encore serait le défaut qu'on corrige ici.
 */
const PRIORITE: readonly Settlement[] = ['unpaid', 'partial', 'paid', 'credited', 'cancelled'];

/** Ce que l'inscription dit d'elle-même. */
export interface EtatDeLInscription {
  /** `registrations.cancellation_status`. */
  annulation: string | null | undefined;
  /** `registrations.payment_status` : seul `cancelled` y compte encore. */
  commande: string | null | undefined;
}

function estAnnulee({ annulation, commande }: EtatDeLInscription): boolean {
  return (!!annulation && annulation.startsWith('cancelled_')) || commande === 'cancelled';
}

/** Commande abandonnée : morte sans annulation, donc absente d'une colonne d'annulation. */
function estCommandeAbandonnee({ annulation, commande }: EtatDeLInscription): boolean {
  return commande === 'cancelled' && !(annulation && annulation.startsWith('cancelled_'));
}

function phraseDeFacture(l: LigneDeReglement): string {
  return `Facture ${l.invoiceNumber} — ${ETATS[l.settlement].phrase}`;
}

/** La facture qui donne son statut à l'inscription, s'il y en a une. */
export function factureRetenue(lignes: readonly LigneDeReglement[]): LigneDeReglement | null {
  let retenue: LigneDeReglement | null = null;
  for (const l of lignes) {
    if (!retenue || PRIORITE.indexOf(l.settlement) < PRIORITE.indexOf(retenue.settlement)) {
      retenue = l;
    }
  }
  return retenue;
}

/**
 * Le statut affiché d'une inscription : « Annulé » d'abord, la facture sinon.
 */
export function reglementDeLInscription(
  inscription: EtatDeLInscription,
  lignes: LignesLues,
): ReglementAffiche {
  if (estAnnulee(inscription)) {
    const titres = (lignes ?? []).map(phraseDeFacture).join('\n');
    return {
      cle: 'inscription_annulee',
      libelle: 'Annulé',
      classe: 'bg-red-100 text-red-800',
      titre: titres ? `Inscription annulée.\n${titres}` : 'Inscription annulée.',
    };
  }
  return reglementDesFactures(lignes);
}

/**
 * Le règlement d'une inscription sur un écran où l'annulation a sa colonne.
 *
 * La facture, même pour une inscription annulée : savoir qu'elle était payée
 * sert à décider d'une réactivation ou d'un remboursement. Sauf la commande
 * abandonnée, que la colonne d'annulation ne montre pas : elle reste
 * « Annulé », comme du temps de `payment_status`.
 */
export function reglementACoteDeLAnnulation(
  inscription: EtatDeLInscription,
  lignes: LignesLues,
): ReglementAffiche {
  if (estCommandeAbandonnee(inscription)) {
    return reglementDeLInscription(inscription, lignes);
  }
  return reglementDesFactures(lignes);
}

/** L'état de la facture d'une inscription, sans regarder son annulation. */
function reglementDesFactures(lignes: LignesLues): ReglementAffiche {
  if (lignes === undefined) {
    return {
      cle: 'lecture',
      libelle: '…',
      classe: 'bg-gray-50 text-gray-400',
      titre: 'Lecture du statut de paiement…',
    };
  }
  if (lignes === null) {
    return {
      cle: 'indisponible',
      libelle: 'Indisponible',
      classe: 'bg-gray-100 text-gray-500',
      titre: 'Le statut de paiement n’a pas pu être lu.',
    };
  }
  const retenue = factureRetenue(lignes);
  if (!retenue) {
    return {
      cle: 'sans_facture',
      libelle: 'Sans facture',
      classe: 'bg-gray-100 text-gray-600',
      titre: 'Aucune facture n’est liée à cette inscription.',
    };
  }
  return {
    cle: retenue.settlement,
    ...LIBELLES[retenue.settlement],
    titre: lignes.map(phraseDeFacture).join('\n'),
  };
}
